/**
 * API contracts shared by both dashboards.
 *
 * Shared pages/components depend ONLY on these interfaces — never on a concrete
 * axios instance or service file. Each app (local-node, cloud-node) provides an
 * implementation and injects it through <ApiProvider>. This is what lets the same
 * page work against the local backend (`/roles`) and the cloud backend
 * (which may node-scope or tunnel-wrap the same call) without any change to the page.
 *
 * Add one interface per domain as pages are extracted into the shared package.
 */

/** A permission string, e.g. "cameras.view". */
export type Permission = string;

export interface Role {
  id: string;
  name: string;
  description?: string;
  /** Permission ids/strings attached to the role. Shape kept loose across nodes. */
  permissions?: Permission[];
  permission_ids?: string[];
  is_system?: boolean;
  [key: string]: unknown;
}

export interface RoleInput {
  name: string;
  description?: string;
  permissionIds?: string[];
}

/** Roles domain — identical endpoints on both nodes (`/roles`). */
export interface RoleApi {
  list(): Promise<Role[]>;
  get(id: string): Promise<Role>;
  create(data: RoleInput): Promise<Role>;
  update(id: string, data: Partial<RoleInput>): Promise<Role>;
  delete(id: string): Promise<unknown>;
  /** Permissions payload shape varies by backend (flat list + grouped); kept loose. */
  getPermissions(): Promise<any>;
}

/** Pagination metadata (local node paginates users; cloud returns the full list). */
export interface PageMeta {
  page: number;
  totalPages: number;
  total: number;
  [key: string]: unknown;
}

/**
 * Canonical user shape used by shared user UI. Each node's adapter maps its backend
 * fields onto this (local `full_name`→`name`, `role_name`→`role`); shared components
 * never see the backend-specific names.
 */
export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  role_id?: string | null;
  is_active?: boolean;
  created_at?: string;
  group_access?: unknown[];
  /** 'cloud' on a local node = provisioned from the cloud (read-only there, no login). */
  user_type?: string;
  [key: string]: unknown;
}

export interface UserListResult {
  data: User[];
  meta?: PageMeta | null;
}

/** Canonical payload the shared user form emits; adapters map it back to each backend. */
export interface UserInput {
  name: string;
  email: string;
  password?: string;
  role_id?: string;
  is_active?: boolean;
}

/** Users domain. Adapters normalize field names and (optionally) pagination. */
export interface UserApi {
  list(params?: { page?: number; limit?: number }): Promise<UserListResult>;
  get(id: string): Promise<User>;
  create(data: UserInput): Promise<unknown>;
  update(id: string, data: Partial<UserInput>): Promise<unknown>;
  delete(id: string): Promise<unknown>;
  /** Roles available to assign to a user, as `{ id, name }`. */
  listRoles(): Promise<Array<{ id: string; name: string; [key: string]: unknown }>>;
}

/**
 * The full set of API domains a host app must provide. Grows as more domains are
 * shared (cameras, groups, recordings, ...). Each new domain adds one property here
 * plus its interface above.
 */
/** A camera group. `list()` may return flat or nested; the page builds the tree. */
export interface Group {
  id: string;
  name: string;
  description?: string | null;
  parent_id?: string | null;
  children?: Group[];
  camera_count?: number;
  [key: string]: unknown;
}

export interface GroupInput {
  name: string;
  description?: string;
  parent_id?: string | null;
}

/**
 * Groups domain. The cloud adapter binds the selected node id (read from localStorage)
 * so node-scoping stays invisible to the shared page; local talks to its backend directly.
 */
export interface GroupApi {
  /** All groups (flat or already nested). The shared page normalizes into a tree. */
  list(): Promise<Group[]>;
  create(data: GroupInput): Promise<unknown>;
  update(id: string, data: Partial<GroupInput>): Promise<unknown>;
  delete(id: string): Promise<unknown>;
  /** Cameras in a group (recursive). */
  getCameras(groupId: string): Promise<any[]>;
}

/** An analytics/AI model. */
export interface Model {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  group_count?: number;
  camera_count?: number;
  [key: string]: unknown;
}

export interface ModelInput {
  name: string;
  description?: string;
}

/** A camera as needed by the model-attach picker. */
export interface CameraRef {
  id: string;
  name: string;
  [key: string]: unknown;
}

/** Models domain (cloud adapter binds the selected node id). */
export interface ModelApi {
  list(params?: { page?: number; limit?: number }): Promise<{ data: Model[]; meta?: PageMeta | null }>;
  create(data: ModelInput): Promise<unknown>;
  update(id: string, data: Partial<ModelInput>): Promise<unknown>;
  delete(id: string): Promise<unknown>;
  attachToCamera(modelId: string, cameraId: string, config: unknown): Promise<unknown>;
  /** Cameras available to attach a model to. */
  listCameras(): Promise<CameraRef[]>;
}

/** A camera found during ONVIF discovery. */
export interface DiscoveredCamera {
  ip_address: string;
  device_name?: string;
  name?: string;
  manufacturer?: string;
  model?: string;
  firmware?: string;
  rtsp_urls?: string[];
  [key: string]: unknown;
}

export interface DiscoveryResults {
  data: DiscoveredCamera[];
  total: number;
}

export interface DiscoveryScanHandlers {
  /** Called with the full current result set as the scan progresses. */
  onResults: (cameras: DiscoveredCamera[], total: number) => void;
  /** Called once when the scan finishes (or fails). */
  onDone: (error?: string) => void;
}

/**
 * Discovery domain. The adapter owns the "how scan results arrive" mechanism — local
 * streams via socket.io, cloud polls a node-scoped endpoint — behind watchScan(), so the
 * shared page just renders results.
 */
export interface DiscoveryApi {
  startScan(subnet: string): Promise<void>;
  getResults(params: { page?: number; limit?: number; search?: string }): Promise<DiscoveryResults>;
  bulkApprove(cameras: DiscoveredCamera[]): Promise<any>;
  /** IPs of already-registered cameras, to mark discovered ones as added. */
  listExistingCameraIps(): Promise<string[]>;
  /** Begin receiving scan results; returns an unsubscribe/stop function. */
  watchScan(handlers: DiscoveryScanHandlers): () => void;
}

// ── Alert Hexagon ───────────────────────────────────────────────────────────

/** A camera as a hexagon: identity + site/group placement + live/recording state. */
export interface HexCamera {
  id: string;
  name: string;
  /** Live stream pipeline is up (running). */
  live: boolean;
  /** Continuous recording pipeline is running. */
  recording: boolean;
  /** Convenience: up if either live or recording is running. Drives the hex base colour
   *  (online vs offline); the two dots show the live/recording breakdown. */
  online: boolean;
  groupId: string | null;
  groupName: string | null;
  siteId: string;
  siteName: string | null;
}

/** Active-alert counts for one camera (drives the hex colour). */
export interface HexCounter {
  critical: number;
  warning: number;
  normal: number;
  total: number;
}

/** One page of active alerts for the docked panel's infinite-scroll list. */
export interface HexAlertPage {
  data: HexAlert[];
  page: number;
  totalPages: number;
  total: number;
}

/** A single alert, normalized across nodes (severity already mapped to 3 tiers). */
export interface HexAlert {
  /** Alert id — needed to drive lifecycle actions (ack/resolve) and to remove a
   *  specific alert from the feed when it's cleared. May be absent on legacy streams. */
  id?: string;
  cameraId: string;
  severity: 'critical' | 'warning' | 'normal';
  eventType?: string;
  alertName?: string;
  description?: string;
  snapshotUrl?: string | null;
  /** Source event id (denormalized). Lets a node lazy-fetch the alert's snapshot when it
   *  isn't a directly-loadable URL (cloud blob-proxies node images through the tunnel). */
  eventId?: string;
  timestamp?: number;
  /** Lifecycle status. The hexagon only shows alerts that still need attention
   *  ('new'); acknowledged/assigned/resolved/cancelled drop off the layout. */
  status?: string;
  /**
   * Bumped when the node reports this alert's snapshot is stored and resolvable
   * (`alert:preview`). Alerts arrive with no image at all — the node's fast lane broadcasts
   * before its storage lane has written the event row — so a card shows the live stream
   * first and the thumbnail only once this changes. AlertCard keys its preview effect on
   * this, which is what re-runs a `loadPreview` that would have been too early to succeed.
   */
  previewReady?: number;
}

/**
 * "This alert's snapshot is now available." Emitted per alert, after the fact.
 * `snapshotUrl` is set by hosts that can serve the image as a plain URL (local); cloud
 * leaves it null and lets `loadPreview` fetch the bytes through the tunnel instead.
 */
export interface HexAlertPreview {
  alertId?: string;
  cameraId?: string;
  snapshotUrl?: string | null;
}

/** Lifecycle transition for one alert (ack/assign/resolve/cancel/reopen). */
export interface HexAlertLifecycle {
  alertId?: string;
  cameraId: string;
  status: string;
  severity?: HexAlert['severity'];
}

/**
 * A node's demo-reset cycle: it wipes every AI event, alert and attendance row on a timer.
 *
 * The board shows this because the alternative reads as a fault — alerts an operator was
 * working through simply disappear, and nothing on screen accounts for it. Announced by the
 * node (heartbeated and re-sent the instant a wipe lands), so `nextResetAt` is the node's
 * own arithmetic and a client that misses a beat still converges on the truth.
 */
export interface DemoResetInfo {
  /** False on every normal deployment — the board shows nothing at all in that case. */
  enabled: boolean;
  /** Cycle length, as configured on the node (DEMO_RESET_INTERVAL_MINUTES). */
  intervalMinutes: number | null;
  /** Epoch ms of the next wipe, or null if the node hasn't said (no countdown then). */
  nextResetAt: number | null;
}

/**
 * The node's wire shape (snake_case, ISO timestamps) → DemoResetInfo. Lives here rather than
 * in each adapter because all three of them — local socket.io, cloud single-node, cloud
 * all-sites — receive the identical payload, and three private copies of "is the cycle on"
 * is how two boards would end up disagreeing about whether a site is a demo.
 *
 * `next_reset_at` is trusted as sent: it is the node's own timer, and recomputing it from an
 * interval plus the client's clock would drift by whatever the two clocks disagree on.
 */
export function toDemoResetInfo(raw: any): DemoResetInfo {
  const minutes = Number(raw?.interval_minutes);
  const next = raw?.next_reset_at ? new Date(raw.next_reset_at).getTime() : NaN;
  return {
    enabled: raw?.enabled === true,
    intervalMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : null,
    nextResetAt: Number.isFinite(next) ? next : null,
  };
}

export interface HexAlertHandlers {
  /** Initial snapshot AND (for pollers) each refresh: full counters + recent feed. */
  onSnapshot: (counters: Record<string, HexCounter>, recent: HexAlert[]) => void;
  /**
   * IS THE FEED ACTUALLY CONNECTED — pushed whenever that changes.
   *
   * A dashboard cannot tell a quiet night from a dead subscription: both are a still panel with
   * old timestamps on it, and only one of them means what is on screen is wrong. Nothing else in
   * this interface can answer it — `onSnapshot` firing says the feed WAS up, and its silence is
   * exactly the ambiguity. So the transport reports its own state: `true` on connect, `false` on
   * drop, for as long as the watch lives.
   *
   * What "connected" means is the transport's own business. A socket host reports the socket;
   * a POLLING host reports whether its last poll succeeded, which is the same claim at that
   * host's resolution. Omitted by hosts that genuinely cannot tell, and a consumer that gets
   * nothing shows no indicator rather than guessing — an indicator that is always green is
   * worse than none, because it is believed.
   */
  onConnection?: (connected: boolean) => void;
  /** A single live alert to merge (local socket stream; pollers may omit). */
  onAlert?: (alert: HexAlert) => void;
  /**
   * A previously-delivered alert's snapshot has finished storing and can now be shown.
   * The page patches the matching alert in place (live tile, card list and recent feed) so
   * its preview resolves without disturbing anything else on screen. Omitted by hosts whose
   * alerts already carry a usable image on arrival.
   */
  onAlertPreview?: (preview: HexAlertPreview) => void;
  /**
   * A live lifecycle change (ack/assign/resolve/cancel). The page removes the alert
   * from the feed and reverts the camera's hex colour when the new status is not 'new'.
   * Local pushes these off the backend `alert:lifecycle` socket; pollers may omit it
   * (their next onSnapshot already excludes cleared alerts).
   */
  onLifecycle?: (change: HexAlertLifecycle) => void;
  /**
   * The node's event store was WIPED — every alert the page is holding is gone (the demo
   * reset TRUNCATEs the event tables on a cycle). A wipe emits no per-alert lifecycle
   * events, so this is the only thing that can tell a board its accumulated counters, cards
   * and live tiles are about rows that no longer exist; without it they survived until a
   * page reload. The page drops all of it and refetches. The adapter follows this with a
   * fresh onSnapshot, so anything still genuinely active (another site, on the all-sites
   * board) comes straight back.
   */
  onCleared?: () => void;
  /**
   * The node's demo-reset cycle changed or was (re)announced — see DemoResetInfo. Fires on
   * connect (the host replays the last announcement), on each heartbeat, and right after a
   * wipe, which is what restarts the board's countdown from the node's clock rather than
   * from the client's guess at it.
   */
  onDemoSchedule?: (info: DemoResetInfo) => void;
  /**
   * Live STREAM running/offline updates (cameraId → running). Keeps the LIVE dot fresh
   * after mount. Fed by the `stream:started`/`stream:error` socket and the overall
   * `camera:status_changed` (streaming ⇒ live up). The page debounces the offline edge.
   */
  onLiveStatus?: (live: Record<string, boolean>) => void;
  /**
   * RECORDING running/offline updates (cameraId → running). Keeps the REC dot fresh.
   * Fed by `recording:started`/`recording:stopped` (is_recording) and the overall
   * `camera:status_changed`. The page debounces the offline edge.
   */
  onRecordingStatus?: (recording: Record<string, boolean>) => void;
}

/**
 * Alert Hexagon domain. The adapter owns the "how alerts arrive" mechanism — local
 * streams via socket.io (+ rollup snapshot), cloud polls a node-scoped endpoint —
 * behind watch(), so the shared page just renders cameras + colours + the alert feed.
 */
export interface HexAlertsApi {
  /** All cameras for this node/app, annotated with site/group + online state. */
  loadCameras(): Promise<HexCamera[]>;
  /** Begin receiving alert counters + recent feed; returns an unsubscribe/stop function. */
  watch(handlers: HexAlertHandlers): () => void;
  /** Paginated list of ACTIVE alerts (status 'new'), latest-first — backs the docked
   *  panel's infinite-scroll card list. Optional: a node that doesn't support it falls
   *  back to the realtime `watch()` feed only (no scroll-to-load). */
  listActiveAlerts?(params: { page: number; limit: number }): Promise<HexAlertPage>;
  /** Lazily resolve a preview image (object URL / data URL) for an alert whose snapshot
   *  isn't a directly-loadable URL — e.g. cloud, which blob-fetches node images through the
   *  tunnel. Returns null when unavailable. Hosts exposing `snapshotUrl` directly omit it.
   *  The caller owns revoking any returned object URL. */
  loadPreview?(alert: HexAlert): Promise<string | null>;
  /** Acknowledge an alert (operator is now on it). Optional: hosts that support the
   *  lifecycle implement it; the alert tiles only render the action when present. */
  acknowledgeAlert?(alertId: string): Promise<void>;
  /** Resolve an alert (terminal). */
  resolveAlert?(alertId: string, note?: string): Promise<void>;
}

// ── Map View (floor-plan layouts) ───────────────────────────────────────────

/** A floor plan / site plan, without its image bytes. */
export interface MapLayoutSummary {
  id: string;
  name: string;
  description?: string | null;
  image_mime: string;
  /** Natural pixel size of the plan. The canvas uses it as its content box and
   *  placements are fractions of it, so pins survive zoom and re-upload. */
  image_width: number;
  image_height: number;
  /** The layout the page opens on. Exactly one layout carries it. */
  is_default: boolean;
  sort_order: number;
  /** Cameras pinned on this layout that the caller can see. */
  camera_count: number;
  updated_at?: string;
  [key: string]: unknown;
}

/**
 * One camera pinned on a layout. `x`/`y` are fractions of the image (0..1).
 *
 * The install fields are surveyed detail an operator adds from the pin's dialog and reads
 * back on hover. `mount_height_m` / `tilt_deg` are nullable on purpose: "not surveyed" is a
 * real state, and defaulting them to 0 would claim a camera sits on the floor aimed level.
 */
export interface MapPlacement {
  camera_id: string;
  x: number;
  y: number;
  /** Where it POINTS: degrees clockwise from north. Drives the pin's direction notch and
   *  the field-of-view cone. */
  rotation?: number;
  /** How WIDE it sees, in degrees — the cone's spread. */
  fov_deg?: number;
  /** Mounting height in metres, or null when not surveyed. */
  mount_height_m?: number | null;
  /** Downward tilt from horizontal in degrees, or null when not surveyed. */
  tilt_deg?: number | null;
  /** Free-text placement note — "above door 3, north-east corner". */
  location_note?: string | null;
  label?: string | null;
  /** Denormalized from the camera row so a pin can be drawn before alert traffic arrives. */
  camera_name?: string;
  camera_status?: string;
}

export interface MapLayoutDetail extends MapLayoutSummary {
  placements: MapPlacement[];
}

/** Where a camera is already pinned — powers the editor's "already on Floor 1" state. */
export interface MapPlacedCamera {
  layout_id: string;
  layout_name: string;
}

export interface MapLayoutInput {
  name: string;
  description?: string;
  imageMime: string;
  /** base64, no `data:` prefix. */
  imageData: string;
  imageWidth: number;
  imageHeight: number;
}

/**
 * Map View domain — floor-plan layouts and the camera pins on them.
 *
 * Node-scoping lives in the adapter, like GroupApi: cloud binds the selected node and
 * tunnels each call; local talks to its backend directly.
 *
 * `imageUrl` is a METHOD rather than a field because the two nodes resolve the plan
 * differently — local returns a plain `/api/map-layouts/:id/image` URL, cloud returns a
 * `data:` URL built from base64 carried over the tunnel (the same split
 * `AttendanceApi.eventImageDataUrl` uses). The page just sets it as `<img src>`.
 */
export interface MapLayoutApi {
  list(): Promise<MapLayoutSummary[]>;
  get(id: string): Promise<MapLayoutDetail>;
  imageUrl(layout: MapLayoutSummary): Promise<string>;
  create(input: MapLayoutInput): Promise<MapLayoutSummary>;
  update(id: string, patch: Partial<MapLayoutInput> & { is_default?: boolean }): Promise<MapLayoutSummary>;
  delete(id: string): Promise<void>;
  /** cameraId → the layout that owns it, across ALL layouts. */
  placedCameras(): Promise<Record<string, MapPlacedCamera>>;
  /**
   * Replace a layout's whole placement set. Rejects with a `MapPlacementConflict` when a
   * camera is already pinned on another layout — a camera belongs to exactly one layout,
   * enforced by a unique constraint on the node's database.
   */
  setPlacements(id: string, placements: MapPlacement[]): Promise<MapLayoutDetail>;
}

/** One camera that blocked a placement save (the 409 body's `error.details`). */
export interface MapPlacementConflict {
  camera_id: string;
  camera_name: string;
  layout_id: string;
  layout_name: string;
}

// ── Access Control ──────────────────────────────────────────────────────────

/** A user's camera access = cameras from these groups ∪ these individual cameras. */
export interface AccessSelection {
  groupIds: string[];
  cameraIds: string[];
}

/**
 * Access Control domain — assign camera GROUPS + individual CAMERAS to a user.
 *
 * Node-scoping is handled inside the adapter (cloud binds the globally-selected
 * node id, mirroring GroupApi/ModelApi); the shared page stays node-unaware and
 * refetches when its `nodeKey` prop changes. Local talks to its backend directly.
 */
export interface AccessControlApi {
  /** Users manageable here (cloud: cloud users with a grant on the selected node). */
  listUsers(): Promise<User[]>;
  /** All groups for the (selected) node — flat or nested; the page trees it. */
  listGroups(): Promise<Group[]>;
  /** All cameras for the (selected) node. */
  listCameras(): Promise<CameraRef[]>;
  /** Cameras in a group, recursive (incl. sub-groups). */
  groupCameras(groupId: string): Promise<CameraRef[]>;
  /** A user's current selection. */
  getAccess(userId: string): Promise<AccessSelection>;
  /** Replace a user's selection. */
  setAccess(userId: string, selection: AccessSelection): Promise<void>;
}

// ── Attendance ──────────────────────────────────────────────────────────────

/** One enrollment photo attached to an employee. */
export interface EmployeePhoto {
  id: string;
  /** 'unknown_event' when the photo came from linking an unknown face event. */
  source?: string;
  [key: string]: unknown;
}

/**
 * Filters for the unknown-face feed.
 *
 * `from` / `to` are UNIX epoch SECONDS, matching the `event_timestamp` column the
 * command-center compares them against — not milliseconds and not an ISO string, either
 * of which silently matches nothing rather than erroring.
 *
 * The window is applied in SQL, not in the browser: the timeline's repair flow asks for a
 * few minutes around a missed sighting, and narrowing client-side would mean paging the
 * whole feed across the tunnel just to throw almost all of it away — and would miss
 * candidates entirely whenever the window fell outside the fetched page.
 */
export interface UnknownFaceQuery {
  limit?: number;
  page?: number;
  /** Inclusive lower bound, epoch seconds. */
  from?: number;
  /** Inclusive upper bound, epoch seconds. */
  to?: number;
}

/**
 * Attendance domain — employees, presence, in/out timeline, enrolment photos and
 * per-camera entry/exit roles. Payloads stay `any`: the command-center returns
 * wide, evolving row shapes and the pages render them defensively.
 *
 * Node-scoping lives in the adapter, like GroupApi: cloud binds the selected node
 * and peels its double envelope (cloud BE wrapper + CC wrapper); local talks to
 * the command-center directly through nginx. Pages take a `nodeKey` prop purely
 * so they refetch when the operator switches node on cloud.
 */
export interface AttendanceApi {
  // Module config
  getConfig(): Promise<any>;
  updateConfig(patch: any): Promise<any>;

  // Presence + timeline
  today(date?: string): Promise<any>;
  timeline(params?: { date?: string; camera_id?: string; search?: string }): Promise<any>;

  // Employees
  listEmployees(params?: any): Promise<any[]>;
  getEmployee(id: string): Promise<any>;
  createEmployee(data: any): Promise<any>;
  updateEmployee(id: string, data: any): Promise<any>;
  /**
   * Remove an employee. Cascades their whole attendance record — days, sessions,
   * sightings, payroll adjustments and enrolment photos — and tells the AI module
   * to drop the identity's face vectors. Not reversible.
   */
  deleteEmployee(id: string): Promise<any>;
  employeeDay(id: string, date?: string): Promise<any>;
  employeeMonthly(id: string, month?: string): Promise<any>;
  /** Raw sightings — returns the paginated envelope `{ data, pagination }`. */
  employeeSightings(id: string, params?: any): Promise<any>;

  // Payroll — org-wide monthly present/absent summary (`month` = YYYY-MM, default current)
  monthlyReport(month?: string): Promise<any>;
  /**
   * Set an employee's hand-entered day counts for a month — leave, sick leave, WFH and days the
   * recognition model missed them. Accepts a subset of the fields (one edited cell) and returns
   * the recomputed `payable_days`.
   *
   * Holiday days are NOT here: they are derived from the org-wide holiday calendar, so the
   * column is read-only in the sheet.
   */
  setPayrollAdjustments(
    month: string,
    employeeId: string,
    patch: {
      unpaid_leave_days?: number;
      paid_leave_days?: number;
      sick_leave_days?: number;
      wfh_days?: number;
      ai_error_days?: number;
      note?: string;
    },
  ): Promise<any>;
  /**
   * Set the same day count on EVERY active employee for a month — the "AI model error for
   * everyone" case, when a camera or the ingest pipeline was down for a day. Overwrites, so
   * saving twice does not pay twice; per-employee edits afterwards behave as normal.
   */
  setPayrollBulkAdjustments(
    month: string,
    patch: {
      unpaid_leave_days?: number;
      paid_leave_days?: number;
      sick_leave_days?: number;
      wfh_days?: number;
      ai_error_days?: number;
    },
  ): Promise<any>;
  /**
   * Declare an org-wide holiday, or rename one already declared on that date. `date` is
   * YYYY-MM-DD. Which employees it earns a payable day for is decided by the server from the
   * date — a Sunday, a day they worked through, or a day before they joined earns nothing.
   */
  addPayrollHoliday(date: string, name: string): Promise<any>;
  /** Withdraw an org-wide holiday. `date` is YYYY-MM-DD. */
  removePayrollHoliday(date: string): Promise<any>;
  /** Override the month's working-day count; `null` clears it back to calendar-minus-Sundays. */
  setPayrollWorkingDays(month: string, workingDays: number | null): Promise<any>;
  /**
   * Payroll Setup: which day types earn a payable day (`count_*` booleans), how much of a day
   * the arguable ones are worth (`weight_*`, a whole or half day), and how long a day must be
   * to count as full or half (`full_day_minutes` / `half_day_minutes`). Accepts any subset.
   * Policy — applies to every month, past ones included.
   */
  setPayrollSettings(settings: Record<string, boolean | number>): Promise<any>;

  // Enrolment photos
  uploadEmployeePhotos(id: string, files: File[]): Promise<any>;
  removeEmployeePhoto(employeeId: string, photoId: string): Promise<any>;
  /** `data:` URL for an enrolment photo, or null when it can't be fetched. */
  employeePhotoDataUrl(photoId: string): Promise<string | null>;

  // Unknown faces → register / link
  listUnknownFaceEvents(params?: UnknownFaceQuery): Promise<any[]>;
  createEmployeeFromEvent(data: any): Promise<any>;
  linkEventToEmployee(employeeId: string, eventId: string): Promise<any>;
  /** `data:` URL for a face event image (`faceCrop` | `alignedFace`). */
  eventImageDataUrl(eventId: string, type?: string): Promise<string | null>;

  // Camera entry/exit roles
  listCameraConfigs(): Promise<any[]>;
  upsertCameraConfig(cameraId: string, data: any): Promise<any>;
  /** The node's cameras — for the timeline filter and the config table. */
  listCameras(): Promise<CameraRef[]>;
}

/**
 * One camera's heatmap coverage over a time range, as `/heatmap/cameras` returns it.
 *
 * Identity is only `camera_id`: heatmap metrics live in the command-center's database and
 * cameras live in the backend's, so there is no join to make server-side. The report page
 * stitches these onto its camera list by id.
 */
export interface HeatmapCameraSummary {
  camera_id: string;
  /** Records received in the range — one per reporting interval. */
  entries: number;
  total_hits: number;
  total_interval_seconds: number;
  /** The observed reporting interval; null when the module never sent one. */
  avg_interval_seconds: number | null;
  first_event_at: string | null;
  last_event_at: string | null;
  grid_rows: number | null;
  grid_cols: number | null;
  cell_size_px: number | null;
}

/**
 * ONE camera's heatmap over a time range: the coverage figures plus the spatial grid the
 * overlay is drawn from. Summed server-side across every record in the range.
 */
export interface HeatmapAggregate {
  /** rows × cols of hit counts. Empty or missing when nothing was reported. */
  spatial_grid: number[][] | null;
  grid_rows: number;
  grid_cols: number;
  total_hits: number;
  total_interval_seconds: number;
  /** Records that went into the sum — one per reporting interval. */
  event_count: number;
}

/**
 * Heatmap domain — the person-activity grids the AI module reports per camera.
 *
 * The live PLAYER the grid is drawn over is still app-local (SFU on the local node, the
 * tunnelled player in the cloud) and reaches the page as a render slot — but the reads are all
 * here, because the page that picks a camera and the page that draws its grid are now one page.
 */
export interface HeatmapApi {
  /** Per-camera coverage. `from`/`to` are epoch ms; omit for all history. */
  cameraSummary(params?: { from?: number; to?: number }): Promise<HeatmapCameraSummary[]>;
  /** One camera's summed grid over a range. `from`/`to` are epoch ms. */
  aggregated(cameraId: string, from: number, to: number): Promise<HeatmapAggregate | null>;
  /** The node's cameras, so cameras with no heatmap data can still be listed. */
  listCameras(): Promise<CameraRef[]>;
  /**
   * Camera ids the heatmap model is attached to, direct or group-inherited (see
   * `cameraIdsForModel`). Optional: where an app can't resolve it the report says a camera has
   * no data, rather than claiming the model is missing.
   */
  heatmapCameraIds?(): Promise<string[]>;
}

export interface TrinetraApi {
  roles: RoleApi;
  users: UserApi;
  groups: GroupApi;
  models: ModelApi;
  discovery: DiscoveryApi;
  hexAlerts: HexAlertsApi;
  mapLayouts: MapLayoutApi;
  accessControl: AccessControlApi;
  attendance: AttendanceApi;
  heatmap: HeatmapApi;
}
